import React, { memo, useState, useRef, useEffect, useCallback } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Card, InputNumber, Button, Space, message, Radio, Progress, Image } from 'antd';
import { ScissorOutlined, PictureOutlined, PlusOutlined, PlayCircleFilled, LoadingOutlined } from '@ant-design/icons';
import NodeLabel from './NodeLabel';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../api/client';
import { useParams } from 'react-router-dom';
import { TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';
import { useNodeTitle } from './useNodeTitle';

const ImageSlicingNode = ({ id, data, selected }) => {
    const { projectId: canvasId } = useParams();
    const { setNodes, getEdges, getNodes } = useReactFlow();

    const onTitleChange = useNodeTitle(id, data);
    const { currentUser } = useAuth();
    const [token, setToken] = useState(null);
    const [hovered, setHovered] = useState(false);

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    useEffect(() => {
        if (currentUser) {
            currentUser.getIdToken().then(setToken);
        }
    }, [currentUser]);

    // --- State ---
    const [mode, setMode] = useState(data.mode || 'even'); // 'even' or 'free'
    const [rows, setRows] = useState(data.rows || 2);
    const [cols, setCols] = useState(data.cols || 2);

    // For free mode: storing relative positions (0 to 1)
    const [horizontalLines, setHorizontalLines] = useState(data.horizontalLines || [0.5]);
    const [verticalLines, setVerticalLines] = useState(data.verticalLines || [0.5]);

    // Image source tracking
    const [imageSrc, setImageSrc] = useState(data.src || null);

    // Generation State
    const [generating, setGenerating] = useState(false);
    const [progress, setProgress] = useState(0);

    // Geometry State
    const [imageRect, setImageRect] = useState({ width: 0, height: 0, top: 0, left: 0 });
    const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });

    const imageContainerRef = useRef(null);
    const imgRef = useRef(null);
    const overlayRef = useRef(null);
    const draggingLineRef = useRef(null); // { type: 'h'|'v', index: number }

    // Refs for latest state (for event handlers/async)
    const linesRef = useRef({ h: horizontalLines, v: verticalLines });
    useEffect(() => { linesRef.current.h = horizontalLines; }, [horizontalLines]);
    useEffect(() => { linesRef.current.v = verticalLines; }, [verticalLines]);

    // --- Data Persistence ---
    const updateData = (updates) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) return { ...node, data: { ...node.data, ...updates } };
            return node;
        }));
        if (data.onSave) data.onSave(id, updates);
    };

    // --- State Sync ---
    useEffect(() => {
        if (data.mode && data.mode !== mode) setMode(data.mode);
        if (data.rows && data.rows !== rows) setRows(data.rows);
        if (data.cols && data.cols !== cols) setCols(data.cols);
        if (!draggingLineRef.current) {
            if (data.horizontalLines && JSON.stringify(data.horizontalLines) !== JSON.stringify(horizontalLines)) setHorizontalLines(data.horizontalLines);
            if (data.verticalLines && JSON.stringify(data.verticalLines) !== JSON.stringify(verticalLines)) setVerticalLines(data.verticalLines);
        }
        if (data.src && data.src !== imageSrc) setImageSrc(data.src);
    }, [data, mode, rows, cols, horizontalLines, verticalLines, imageSrc]);

    // --- Connection handling ---
    useEffect(() => {
        const edges = getEdges();
        const nodes = getNodes();
        const targetEdge = edges.find(e => e.target === id && e.targetHandle === 'input-image');
        if (targetEdge) {
            const sourceNode = nodes.find(n => n.id === targetEdge.source);
            if (sourceNode?.data?.src && sourceNode.data.src !== imageSrc) {
                setImageSrc(sourceNode.data.src);
                updateData({ src: sourceNode.data.src });
            }
        }
    }, [getEdges, getNodes, id, imageSrc]);

    // --- Geometry Calculation ---
    // Optimized: Throttle resize updates using requestAnimationFrame to prevent layout thrashing
    const rafRef = useRef(null);

    const updateImageRect = useCallback(() => {
        if (!imageContainerRef.current || !naturalSize.width) return;

        // Cancel any pending frame
        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        rafRef.current = requestAnimationFrame(() => {
            const container = imageContainerRef.current;
            if (!container) return;

            const cWidth = container.clientWidth;
            const cHeight = container.clientHeight;

            // Calculate constrained dimensions (contain)
            const imageRatio = naturalSize.width / naturalSize.height;
            const containerRatio = cWidth / cHeight;

            let renderWidth, renderHeight;

            if (containerRatio > imageRatio) {
                // Container is wider than image -> limited by height
                renderHeight = cHeight;
                renderWidth = renderHeight * imageRatio;
            } else {
                // Container is taller than image -> limited by width
                renderWidth = cWidth;
                renderHeight = renderWidth / imageRatio;
            }

            const left = (cWidth - renderWidth) / 2;
            const top = (cHeight - renderHeight) / 2;

            setImageRect({ width: renderWidth, height: renderHeight, top, left });
        });
    }, [naturalSize]);

    useEffect(() => {
        updateImageRect();
        const ro = new ResizeObserver(updateImageRect);
        if (imageContainerRef.current) ro.observe(imageContainerRef.current);
        return () => {
            ro.disconnect();
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [updateImageRect]);

    const handleImageLoad = (e) => {
        setNaturalSize({ width: e.target.naturalWidth, height: e.target.naturalHeight });
    };


    // --- Drag Logic ---
    useEffect(() => {
        const handleWindowMouseMove = (e) => {
            if (!draggingLineRef.current || !overlayRef.current) return;

            const rect = overlayRef.current.getBoundingClientRect();
            // Mouse relative to the overlay's current screen position/size
            const relX = e.clientX - rect.left;
            const relY = e.clientY - rect.top;

            const { type, index } = draggingLineRef.current;

            if (type === 'h') {
                const val = Math.max(0, Math.min(1, relY / rect.height));

                setHorizontalLines(prev => {
                    const next = [...prev];
                    next[index] = val;
                    return next;
                });
                if (linesRef.current) {
                    const next = [...(linesRef.current.h || [])];
                    next[index] = val;
                    linesRef.current.h = next;
                }
            } else {
                const val = Math.max(0, Math.min(1, relX / rect.width));

                setVerticalLines(prev => {
                    const next = [...prev];
                    next[index] = val;
                    return next;
                });
                if (linesRef.current) {
                    const next = [...(linesRef.current.v || [])];
                    next[index] = val;
                    linesRef.current.v = next;
                }
            }
        };

        const handleWindowMouseUp = () => {
            if (draggingLineRef.current) {
                if (draggingLineRef.current.type === 'h') {
                    updateData({ horizontalLines: linesRef.current.h });
                } else {
                    updateData({ verticalLines: linesRef.current.v });
                }
                draggingLineRef.current = null;
            }
        };

        window.addEventListener('mousemove', handleWindowMouseMove);
        window.addEventListener('mouseup', handleWindowMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleWindowMouseMove);
            window.removeEventListener('mouseup', handleWindowMouseUp);
        };
    }, [imageRect]);

    const handleDragStart = (e, type, index) => {
        e.stopPropagation();
        e.preventDefault();
        draggingLineRef.current = { type, index };
    };

    const addLine = (type) => {
        if (type === 'h') {
            const newLines = [...horizontalLines, 0.5];
            setHorizontalLines(newLines);
            updateData({ horizontalLines: newLines });
        } else {
            const newLines = [...verticalLines, 0.5];
            setVerticalLines(newLines);
            updateData({ verticalLines: newLines });
        }
    };

    // --- Slicing Logic ---
    const handleSlice = async () => {
        if (!imageSrc || !naturalSize.width) {
            message.warning("No image loaded to slice.");
            return;
        }

        setGenerating(true);
        setProgress(0);

        try {
            // 1. Calculate slice coordinates
            let xCuts = [], yCuts = [];

            if (mode === 'even') {
                // 0, 1/cols, 2/cols ... 1
                for (let i = 0; i <= cols; i++) xCuts.push(i / cols);
                for (let i = 0; i <= rows; i++) yCuts.push(i / rows);
            } else {
                // Free mode: 0, sorted lines, 1
                xCuts = [0, ...[...verticalLines].sort((a, b) => a - b), 1];
                yCuts = [0, ...[...horizontalLines].sort((a, b) => a - b), 1];
            }

            // Remove duplicates close to each other? For now assume user intention.

            // 2. Load image into Canvas with CORS handling
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // We need a robust loading strategy: Proxy -> Direct Fallback
            let img = new window.Image();
            img.crossOrigin = "anonymous";

            try {
                // Try Proxy First - Now with Token
                const tokenParam = token ? `&token=${token}` : '';
                const proxyUrl = `${api.defaults.baseURL}/storage/proxy?url=${encodeURIComponent(imageSrc)}${tokenParam}`;
                img.src = proxyUrl;

                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                });
            } catch (proxyError) {
                console.warn("Proxy load failed for slicing, attempting direct load...", proxyError);
                try {
                    // Fallback to direct load
                    img = new window.Image();
                    img.crossOrigin = "anonymous";
                    img.src = imageSrc;

                    await new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = reject;
                    });
                } catch (directError) {
                    throw new Error("Failed to load image via both Proxy and Direct methods. CORS or Network issue.");
                }
            }

            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            // Don't draw whole image, just extract directly? Or draw once and slice.
            // Draw once is easier for getImageData or toDataURL crop.
            // Actually, we can just draw PARTS to a temp canvas to export.

            const totalSlices = (xCuts.length - 1) * (yCuts.length - 1);
            let processed = 0;
            const newGeneratedImages = [];

            for (let i = 0; i < yCuts.length - 1; i++) {
                for (let j = 0; j < xCuts.length - 1; j++) {
                    const x1 = xCuts[j] * img.naturalWidth;
                    const y1 = yCuts[i] * img.naturalHeight;
                    const w = (xCuts[j + 1] - xCuts[j]) * img.naturalWidth;
                    const h = (yCuts[i + 1] - yCuts[i]) * img.naturalHeight;

                    if (w < 1 || h < 1) continue; // Skip empty slices

                    // Create temp canvas for slice
                    const sliceCanvas = document.createElement('canvas');
                    sliceCanvas.width = w;
                    sliceCanvas.height = h;
                    const sCtx = sliceCanvas.getContext('2d');

                    sCtx.drawImage(img, x1, y1, w, h, 0, 0, w, h);

                    // To Blob
                    const blob = await new Promise(resolve => sliceCanvas.toBlob(resolve, 'image/png'));

                    // Upload
                    const formData = new FormData();
                    formData.append('file', blob, `slice_${i}_${j}_${Date.now()}.png`);
                    formData.append('bucket', 'media-generations');

                    const uploadRes = await api.post('/storage/upload', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });

                    const finalUrl = uploadRes.data.url;

                    const genRecord = {
                        url: finalUrl,
                        width: w,
                        height: h,
                        row: i,
                        col: j
                    };
                    newGeneratedImages.push(genRecord);

                    // Save Record to DB (optional, but good for tracking)
                    // We can reuse the `generated-media` endpoint if we want them to show in the media drawer
                    try {
                        await api.post(`/canvases/${canvasId}/generated-media`, {
                            generation_id: crypto.randomUUID(),
                            node_id: id,
                            type: 'image',
                            url: finalUrl,
                            prompt: `Slice ${i}-${j} of source`,
                            provider: 'system',
                            model: 'slicer',
                            size: blob.size,
                            metadata: { width: w, height: h, format: 'png' },
                            system: { id: 'system', email: 'system@pxlplygrnd.com' }
                        });
                    } catch (err) {
                        console.error("Failed to log generation", err);
                    }

                    processed++;
                    setProgress(Math.round((processed / totalSlices) * 100));
                }
            }

            // Update Node Data
            updateData({ generatedImages: newGeneratedImages });
            message.success(`Successfully created ${newGeneratedImages.length} slices.`);

        } catch (error) {
            console.error('Slicing error:', error);
            message.error('Failed to slice image. Check CORS or server status.');
        } finally {
            setGenerating(false);
            setProgress(0);
        }
    };


    // --- Rendering ---
    const renderEvenLines = () => {
        const lines = [];
        for (let i = 1; i < rows; i++) {
            lines.push(
                <div key={`h-${i}`} style={{
                    position: 'absolute', top: `${(i / rows) * 100}%`, left: 0, width: '100%', height: 2,
                    background: 'rgba(255, 0, 0, 0.8)', pointerEvents: 'none', zIndex: 10
                }} />
            );
        }
        for (let i = 1; i < cols; i++) {
            lines.push(
                <div key={`v-${i}`} style={{
                    position: 'absolute', left: `${(i / cols) * 100}%`, top: 0, height: '100%', width: 2,
                    background: 'rgba(255, 0, 0, 0.8)', pointerEvents: 'none', zIndex: 10
                }} />
            );
        }
        return lines;
    };

    const renderFreeLines = () => {
        return (
            <>
                {horizontalLines.map((pos, idx) => (
                    <div key={`h-${idx}`}
                        onMouseDown={(e) => handleDragStart(e, 'h', idx)}
                        style={{
                            position: 'absolute', top: `${pos * 100}%`, left: 0, width: '100%', height: 10, marginTop: -5,
                            cursor: 'row-resize', zIndex: 20
                        }}>
                        <div style={{ width: '100%', height: 2, background: 'cyan', marginTop: 4 }} />
                    </div>
                ))}
                {verticalLines.map((pos, idx) => (
                    <div key={`v-${idx}`}
                        onMouseDown={(e) => handleDragStart(e, 'v', idx)}
                        style={{
                            position: 'absolute', left: `${pos * 100}%`, top: 0, height: '100%', width: 10, marginLeft: -5,
                            cursor: 'col-resize', zIndex: 20
                        }}>
                        <div style={{ height: '100%', width: 2, background: 'cyan', marginLeft: 4 }} />
                    </div>
                ))}
            </>
        );
    };

    const handleModeChange = (e) => {
        const newMode = e.target.value;
        setMode(newMode);
        updateData({ mode: newMode });
    };

    return (
        <div
            style={{ position: 'relative', height: '100%', width: '100%' }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer isVisible={selected} minWidth={300} minHeight={300}
                onResizeEnd={(e, p) => data.onResize && data.onResize(id, { ...p })} />

            <NodeLabel
                title={data.nodeTitle || "Image Slicer"}
                iconType="scissor"
                color="#ff4d4f"
                onTitleChange={onTitleChange}
            />

            {/* Play Button */}
            <Button
                type="primary"
                shape="circle"
                icon={generating ? <LoadingOutlined style={{ fontSize: 72 }} /> : <PlayCircleFilled style={{ fontSize: 72 }} />}
                onClick={handleSlice}
                disabled={generating || !imageSrc}
                style={{
                    position: 'absolute',
                    right: -180,
                    bottom: 0,
                    width: 150,
                    height: 150,
                    zIndex: 100,
                    boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
                    border: '12px solid #161b22',
                    background: '#ff4d4f',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
            />

            <Handle type="target" position={Position.Left} id="input-image" style={getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible)} />

            <Card
                size="small"
                bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
                style={{ width: '100%', height: '100%', border: '1px solid #30363d', background: '#161b22', overflow: 'hidden', borderRadius: 8 }}
            >
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #30363d', background: '#0d1117' }}>
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                        <Radio.Group value={mode} onChange={handleModeChange} size="small" buttonStyle="solid">
                            <Radio.Button value="even">Even Spacing</Radio.Button>
                            <Radio.Button value="free">Free Sliding</Radio.Button>
                        </Radio.Group>

                        {mode === 'even' && (
                            <Space>
                                <span style={{ color: '#8b949e', fontSize: 12 }}>Rows:</span>
                                <InputNumber min={1} max={10} value={rows} onChange={(v) => { setRows(v); updateData({ rows: v }); }} size="small" style={{ width: 60 }} />
                                <span style={{ color: '#8b949e', fontSize: 12 }}>Cols:</span>
                                <InputNumber min={1} max={10} value={cols} onChange={(v) => { setCols(v); updateData({ cols: v }); }} size="small" style={{ width: 60 }} />
                            </Space>
                        )}

                        {mode === 'free' && (
                            <Space>
                                <Button size="small" icon={<PlusOutlined />} onClick={() => addLine('h')}>H-Line</Button>
                                <Button size="small" icon={<PlusOutlined />} onClick={() => addLine('v')}>V-Line</Button>
                            </Space>
                        )}
                    </Space>
                </div>

                <div ref={imageContainerRef} className="nodrag"
                    style={{ flex: 1, position: 'relative', background: '#21262d', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    {generating && (
                        <div style={{ position: 'absolute', zIndex: 50, color: '#fff', background: 'rgba(0,0,0,0.7)', borderRadius: 8, padding: 12 }}>
                            <Space direction="vertical" align="center">
                                <LoadingOutlined style={{ fontSize: 24 }} />
                                <span>Slicing... {progress}%</span>
                            </Space>
                        </div>
                    )}

                    {imageSrc ? (
                        <>
                            <img ref={imgRef} src={imageSrc} onLoad={handleImageLoad} alt="slice-target"
                                style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                                loading="lazy"
                                decoding="async"
                            />
                            {/* Constrained Overlay */}
                            <div
                                ref={overlayRef}
                                style={{
                                    position: 'absolute',
                                    left: imageRect.left,
                                    top: imageRect.top,
                                    width: imageRect.width,
                                    height: imageRect.height,
                                    zIndex: 10
                                }}>
                                {mode === 'even' ? renderEvenLines() : renderFreeLines()}
                            </div>
                        </>
                    ) : (
                        <div style={{ textAlign: 'center', color: '#8b949e' }}>
                            <PictureOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                            <div>Connect Image source</div>
                        </div>
                    )}
                </div>
            </Card>

            {/* Thumbnail Gallery (Right Side) */}
            {data.generatedImages && data.generatedImages.length > 0 && (
                <div className="nodrag" style={{
                    position: 'absolute',
                    top: 50,
                    right: -136, // Push out to right side
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    padding: 4,
                    maxHeight: '100%',
                    overflowY: 'auto'
                }}>
                    <Image.PreviewGroup>
                        {data.generatedImages.map((img, idx) => (
                            <div
                                key={idx}
                                style={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: 6,
                                    border: '2px solid transparent',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    background: '#161b22',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                    marginBottom: 0,
                                    position: 'relative'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#1890ff'}
                                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
                            >
                                <Image
                                    src={img.url}
                                    width={120}
                                    height={120}
                                    style={{ objectFit: 'contain' }}
                                    alt={`slice-${idx}`}
                                    loading="lazy"
                                    decoding="async"
                                />
                                <div style={{
                                    position: 'absolute', bottom: 0, left: 0, right: 0,
                                    background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10, padding: 2, textAlign: 'center',
                                    pointerEvents: 'none'
                                }}>
                                    Slice {idx + 1}
                                </div>
                            </div>
                        ))}
                    </Image.PreviewGroup>
                </div>
            )}
        </div>
    );
};

export default memo(ImageSlicingNode);
